<template>
  <modal ref="modal">
    <span slot="activator">
      <slot name="activator"></slot>
    </span>
    <div slot="header">
      {{ $t('Convert cart to an order') }}

      <div class="price ui right floated header blue">
        {{ finalPrice | price }}
      </div>
    </div>
    <div slot="toolbar">
      <div class="ui left aligned">
        <button
          :class="{ loading: loading }"
          :disabled="createOrderButtonIsDisabled"
          class="ui button green"
          @click="createOrder"
        >
          {{ $t('Create') }}
        </button>
      </div>
    </div>
    <div class="content">
      <vertical-table
        :columns="order_columns"
        name_class="four wide"
        table_class="ui celled table form"
      >
        <div slot="delivery_address">
          <div class="inline field">
            <div class="ui checkbox">
              <input
                id="delivery_to_shop"
                v-model="order.options.shop_delivery"
                type="checkbox"
                tabindex="0"
              />
              <label for="delivery_to_shop" class="cursor">
                {{ $t('Deliver to shop') }}
              </label>
            </div>
          </div>
          <div
            v-if="!order.options.shop_delivery"
            class="four wide column aligned"
          >
            <select
              v-model="order.delivery_address"
              :disabled="order.options.shop_delivery"
              class="ui fluid dropdown selection"
            >
              <option
                v-for="address in currentCustomer.addresses"
                :key="address.id"
                :value="address"
              >
                {{ address.alias + ', ' + address.address1 + ', ' + address.phone_mobile }}
              </option>
            </select>
            <address-modal>
              <button slot="activator" class="ui blue button">
                <i class="add icon"></i>
                {{ $t('Add a new address') }}
              </button>
            </address-modal>
          </div>
        </div>

        <div slot="phone_mobile" class="flex">
          <div>
            <input-field
              v-model="order.phone_mobile"
              validate="required"
              name="Phone (Mobile)"
              input_type="text"
            ></input-field>
          </div>
          <div>
            <template v-for="(phone, index) in phones">
              <span
                :key="'phone_select_' + index"
                :class="{ green: phone == order.phone_mobile }"
                class="ui label"
                @click="order.phone_mobile = phone"
                >{{ phone }}</span
              >
            </template>
          </div>
        </div>
        <div slot="reductions">
          <div
            v-for="(discount, index) in discounts"
            :key="index"
            v-if="!discount.generic || discount.isSearchedFromVoucher"
            class="inline field"
          >
            <div class="ui checkbox">
              <input
                :id="'discount_' + discount.name"
                v-model="order.reductions[discount.name]"
                type="checkbox"
                tabindex="0"
                :disabled="
                  isFreeShippingVoucher(discount) &&
                  freeShippingVoucherIsDisabled
                "
              />
              <template>
                <label :for="'discount_' + discount.name" class="cursor">
                  {{ discount.label }}
                  {{
                    isFreeShippingVoucher(discount)
                      ? freeShippingVoucherDisabledMessage
                      : ''
                  }}
                  </label>
              </template>
            </div>
          </div>
          <div v-if="currentCustomer.group_reduction > 0">
            {{ $t('This user is a privilieged member of group ') }}

            <span style="color: green">{{ currentCustomer.group_name }}</span>
            , {{ $t('having a reduction of ') }}

            <span style="color: green"
              >{{ currentCustomer.group_reduction }}%</span
            >
            !
          </div>
        </div>
        <input-field
          slot="discount"
          v-model="order.discount"
          :max="cartAmount"
          validate="decimal"
          name="Discount"
          size="eight wide"
          placeholder
          input_type="number"
        ></input-field>
        <div slot="voucher">
          <div>
            <input-field
              v-model="order.voucher"
              :icon="getvoucherIcons()"
              class-names="ui fluid icon input"
              placeholder
              @input="voucherCheck"
            >
            </input-field>
            <span v-if="voucher">{{ voucher.label }}</span>
            <span v-if="voucherError" class="error">{{ voucherError }}</span>
          </div>
        </div>
        <input-field
          slot="comment"
          v-model="order.comment"
          :rows="2"
          name="Comment"
          size="eight wide"
          placeholder
          input_type="textarea"
        ></input-field>
        <span slot="total_price" class="total-price">
          <span class="price-item product-price">
            <span class="value">{{ cartAmount }}</span>
            <span class="tooltip">{{ $t('Product price') }}</span>
          </span>
          <span
            v-if="groupUserReduction"
            class="price-item group-user-reduction"
          >
            <span class="value">{{ groupUserReduction }}</span>
            <span class="tooltip">{{ $t('Group user reduction') }} </span>
          </span>
          <span v-if="vouchersByPercent" class="price-item vouchers-by-percent">
            <span class="value">{{ vouchersByPercent }}</span>
            <span class="tooltip">{{ $t('Vouchers by percent') }} </span>
          </span>
          <span v-if="vouchersByAmount" class="price-item vouchers-by-amount">
            <span class="value">{{ vouchersByAmount }}</span>
            <span class="tooltip">{{ $t('Vouchers by amount') }} </span>
          </span>
          <span
            v-if="priceAfterReduction != cartAmount"
            class="price-item price-after-reduction"
          >
            <span class="value">={{ priceAfterReduction }}</span>
            <span class="tooltip">
              {{ $t('Price after reduction') }}
            </span>
          </span>
          <span class="price-item delivery-price">
            <span class="value">+{{ deliveryPrice }}</span>
            <span class="tooltip">
              {{ $t('Delivery price') }}
            </span>
          </span>
          <span class="price-item final-price">
            <span class="value">={{ finalPrice }} &euro;</span>
            <span class="tooltip">
              {{ $t('Final price') }}
            </span>
          </span>
        </span>
      </vertical-table>
    </div>
    <popup-modal
      :cancel_text="null"
      :index="order_confirmed"
      icon="checkmark"
      confirm_text="ok"
      confirm_class="ui positive right labeled icon button"
      text="Thank you for your order !"
      @delete="closeConfirmation(true)"
    ></popup-modal>
    <popup-modal
      :cancel_text="null"
      :text="'An error occured during your order.<br/>' + error_message"
      :index="error_order"
      icon="checkmark"
      confirm_text="Ok, I understand"
      confirm_class="ui positive right labeled icon button"
      @delete="closeConfirmation(false)"
    ></popup-modal>
  </modal>
</template>

<style scoped>
.price {
  padding-right: 50px;
}

.cursor {
  cursor: pointer;
}

.flex {
  display: flex;
}

.flex .label {
  padding: 10px;
  margin: 3px 0 3px 15px;
  cursor: pointer;
}

select {
  width: 100% !important;
  padding-top: 8px !important;
}

select[disabled='disabled'] {
  background-color: gainsboro !important;
}

select[disabled='disabled']:hover {
  cursor: default;
}

.total-price {
  display: flex;
  justify-content: flex-start;
  height: 72px;
}

.price-item {
  position: relative;
  margin: 0 45px 45px 0;
}

.product-price .value {
  color: blue;
}

.group-user-reduction .value {
  color: green;
}

.vouchers-by-percent .value {
  color: green;
}

.vouchers-by-amount .value {
  color: green;
}

.price-after-reduction .value {
  color: orangered;
}

.delivery-price .value {
  color: purple;
}

.final-price .value {
  color: #2185d0;
}

.tooltip {
  color: gray;
  text-align: left;
  padding: 20px 0;
  position: absolute;
  z-index: 1;
  top: -5px;
  left: 0;
}

.voucher-description {
  display: flex;
  justify-content: flex-start;
}

.voucher-disabled-hint {
  color: gray;
}
</style>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import moment from 'moment'
import { round } from 'lodash'
import AddressModal from './AddressModal'

export default {
  name: 'OrderModal',
  components: {
    'address-modal': AddressModal
  },
  props: {},
  data() {
    return {
      loading: false,
      voucher: false,
      order_confirmed: null,
      error_order: null,
      error_message: '',
      discounts: [],
      voucherError: null,
      voucherIcons: {
        success: 'icon check green',
        error: 'icon close error'
      },
      freeShippingVoucherIsDisabled: false,
      order: {
        delivery_address: {},
        phone_mobile: '',
        reductions: {},
        options: {
          shop_delivery: false
        },
        discount: null,
        voucher: null,
        comment: ''
      },
      order_columns: [
        {
          id: 'delivery_address',
          name: 'Delivery address'
        },
        {
          id: 'phone_mobile',
          name: 'Phone (Mobile)'
        },
        {
          id: 'reductions',
          name: 'Discounts'
        },
        {
          id: 'discount',
          name: 'Manual discount'
        },
        {
          id: 'voucher',
          name: 'Voucher'
        },
        {
          id: 'comment',
          name: 'Comment'
        },
        {
          id: 'total_price',
          name: 'Total price'
        }
      ]
    }
  },
  computed: {
    ...mapGetters({
      currentCustomer: 'getCurrentCustomer',
      pricePrecision: 'getPricePrecision',
      cartAmount: 'getCartAmount',
      shopAddress: 'getShopAddress'
    }),
    phones() {
      const phones = []
      this.currentCustomer.addresses.forEach((address) => {
        if (address.phone_mobile && !phones.includes(address.phone_mobile)) {
          phones.push(address.phone_mobile)
        }
      })
      return phones
    },
    reduction() {
      // Reduction can be in percentage and/or in amount
      const reduction = { amount: [], percents: [] }

      // Separate the type of reductions selected into each type
      Object.keys(this.order.reductions).forEach((key) => {

        // Ignore empty key set
        if (!this.order.reductions[key]) return

        // Retrieve the corresponding discount in the list
        const discount = this.discounts.find((element) => element.name === key)

        // Ignore if the cart rule found is not of type discount
        if (!discount || discount.type !== 'discount') return

        // Add the amount if exists to the list of amount reduction.
        if (discount.reduction_amount > 0)
          reduction.amount.push(discount.round_reduction_amount)

        // Add the percentage if exists to the list of percentage reduction.
        if (discount.reduction_percent > 0)
          reduction.percents.push(discount.reduction_percent)
      })

      // Add the manual discount to the list of amount reduction.
      const orderDiscount = parseInt(
        this.order.discount ? this.order.discount : 0,
        10
      )
      if (orderDiscount > 0) reduction.amount.push(orderDiscount)

      return reduction
    },
    deliveryPrice() {
      let delivery = 9
      /* Delivering to shop. */
      if (this.order.options.shop_delivery) {
        delivery = 0
      } else if (this.priceAfterReduction >= 150) {
        /* Price above 150 €, free delivery. */
        delivery = 0
      }

      // Delivery discounts
      if (!this.order.options.shop_delivery) {
        Object.keys(this.order.reductions).forEach((key) => {
          if (!this.order.reductions[key]) return
          if (!this.discounts) return
          const discount = this.discounts.filter(
            (element) => element.name === key
          )[0]
          if (!discount || discount.type !== 'delivery') return
          delivery -= discount.round_reduction_amount
        })
      }

      return delivery
    },

    priceAfterReduction() {
      // Calculate the total reduction percentage
      const reductionPercents = this.reduction.percents.reduce(
        (x, y) => parseFloat(x, 10) + parseFloat(y, 10),
        0
      )

      // Retrieve the group reduction percentage
      const groupReduction = parseFloat(this.currentCustomer.group_reduction || 0, 10)

      // Calculate the price after all the reduction percentage applied to the product price.
      const productPriceAfterPercent =
        this.cartAmount *
        (1 - groupReduction / 100) *
        (1 - reductionPercents / 100)

      // Calculate the price after the reduction amount
      const reductionAmount = this.reduction.amount.reduce((x, y) => x + y, 0)

      // Calculate the price after the reduction
      const priceAfterReduction = round(
        Math.max(Math.max(productPriceAfterPercent, 0) - reductionAmount, 0),
        this.pricePrecision
      )
      return priceAfterReduction
    },
    finalPrice() {
      return round(
        Math.max(this.priceAfterReduction + this.deliveryPrice, 0),
        this.pricePrecision
      )
    },
    groupUserReduction() {
      return parseFloat(this.currentCustomer.group_reduction) > 0
        ? `*(1-${this.currentCustomer.group_reduction}%)`
        : ''
    },
    vouchersByPercent() {
      if (this.reduction.percents.length === 0) return ''
      let reductionPrecents = '1'
      this.reduction.percents.forEach((percent) => {
        reductionPrecents += `-${percent}%`
      })
      return `*(${reductionPrecents})`
    },
    vouchersByAmount() {
      if (this.reduction.amount.length === 0) return ''
      let vouchersByAmount = 0
      vouchersByAmount = '-'
      this.reduction.amount.forEach((amount, index) => {
        if (index === 0) {
          vouchersByAmount += `${amount}`
        } else {
          vouchersByAmount += `-${amount}`
        }
      })
      return vouchersByAmount
    },
    priceReachedFreeShippingLimit() {
      return this.priceAfterReduction >= 150
    },
    freeShippingVoucherDisabledMessage() {
      if (this.priceReachedFreeShippingLimit)
        return ' *Pour toute commande au-dessus de 150€, les frais de port sont offerts, pas de coupon nécessaire.'
      if (this.order.options.shop_delivery)
        return ' *Livraison à la boutique, les frais de port sont offerts, pas de coupon nécessaire.'
      return ''
    },
    createOrderButtonIsDisabled() {
      // Content loading
      if(this.loading) {
        return true;
      }

      // No phone number set
      if(!this.order.phone_mobile) {
        return true;
      }

      // No address selected
      if(!this.order.options.shop_delivery && !this.order.delivery_address.id){
        return true;
      }

      return false;
    }
  },
  mounted() {
    this.order.phone_mobile = this.phone_mobile
    this.currentCustomer.group_reduction = parseFloat(
      this.currentCustomer.group_reduction,
      10
    )
  },
  methods: {
    open() {
      this.$refs.modal.open()
      this.getDiscounts()
    },
    close() {
      this.$refs.modal.close()
      this.order.delivery_address = {}
      this.order.phone_mobile = ''
      this.order.reductions = {}
      this.order.reductions.parrainage = false
      this.order.options.shop_delivery = false
      this.order.discount = null
      this.order.voucher = null
      this.order.comment = ''
      this.voucher = false
      this.voucherError = null
    },
    uncheckFreeShippingVoucherAndDisableIt() {
      const freeShippingVoucherName = 'LIVRAISON GRATUITE'
      if (this.order && this.order.reductions[freeShippingVoucherName]) {
        this.$set(this.order.reductions, freeShippingVoucherName, false)
      }
      this.freeShippingVoucherIsDisabled = true
    },
    refreshFreeShippingVoucherDisplay() {
      if (
        !this.order.options.shop_delivery &&
        !this.priceReachedFreeShippingLimit
      ) {
        this.freeShippingVoucherIsDisabled = false
      }
      else if(!this.order.options.shop_delivery && this.priceReachedFreeShippingLimit )
      {
        this.freeShippingVoucherIsDisabled = false
      }
      else {
        this.uncheckFreeShippingVoucherAndDisableIt()
      }
    },
    closeConfirmation(close) {
      this.order_confirmed = null
      this.error_order = null
      this.error_message = null
      if (close) {
        this.$emit('switchToOrdersTab')
        this.close()
      }
    },
    createOrder() {
      this.error_message = ''
      if (this.order.options.shop_delivery) {
        this.order.delivery_address = this.shopAddress
        this.order.delivery_address.phone = this.order.phone_mobile;
      }

      this.loading = true
      const self = this
      const { order } = this
      order.id_customer = this.currentCustomer.id
      order.products = this.currentCustomer.cart.products
      const discounts = {}
      Object.keys(order.reductions).forEach((key) => {
        if (!order.reductions[key]) return
        const discount = this.discounts.find((element) => element.name === key)
        if (!discount) return
        if (discount.type === 'delivery' && this.order.options.shop_delivery)
          return
        discounts[key] = discount
      })
      order.reductions = discounts
      if (order.voucher) {
        order.reductions['Remise boutique'] = {
          name: 'Remise boutique',
          code: null,
          reduction_amount: order.voucher
        }
      }

      order.date = moment().format('MMM Do YY')
      axios
        .post('/order', order)
        .then((response) => {
          this.loading = false
          if (response.data.success) {
            self.order_confirmed = true
            self.$store.dispatch('newOrder', order)
            self.currentCustomer.cart.products = []

            axios
              .get(`/customer/${this.currentCustomer.id}/orders`)
              .then((response2) => {
                this.$store.dispatch(
                  'setCurrentCustomerOrders',
                  response2.data.orders
                )
                this.$store.dispatch(
                  'setCurrentCustomer',
                  this.$store.getters.getCurrentCustomer
                )
              })

            this.$emit('created')
            return
          }

          if (response.data.error) {
            this.error_message = response.data.error
          }
          self.error_order = true
          // eslint-disable-next-line
          console.log(response.data)
        })
        .catch((e) => {
          // eslint-disable-next-line
          console.log(e)
          if (e.response.data.error) {
            this.error_message = e.response.data.error
          }
          this.loading = false
          self.error_order = true
        })
    },
    voucherCheck(code) {
      const payload = {
        voucher: code,
        id_customer: this.currentCustomer.id,
        cart_amount: this.cartAmount
      }
      this.voucher = null
      this.voucherError = null

      if (code.length < 3) return
      axios
        .post('voucher/validate', payload)
        .then((response) => {
          const voucherIndex = this.discounts.findIndex(
            (discount) => discount.code === response.data.code
          )
          if (voucherIndex < 0) {
            this.voucherError =
              'This voucher was not found in available discounts for current customer. Please close modal and try again.'
            return
          }
          const voucherDiscount = this.discounts[voucherIndex]
          voucherDiscount.isSearchedFromVoucher = true
          this.$set(this.discounts, voucherIndex, voucherDiscount)
          this.addDiscountToCart(voucherDiscount)
          this.voucher = null
          this.order.voucher = null
        })
        .catch((error) => {
          this.voucherError = error.response.data.error
        })
    },
    isFreeShippingVoucher(discount) {
      return discount && discount.name === 'LIVRAISON GRATUITE'
    },
    getDiscounts() {
      const payload = {
        id_customer: this.currentCustomer.id,
        products: this.currentCustomer.cart.products
      }
      const self = this
      this.freeShippingVoucherIsDisabled = true
      axios.post('discounts', payload).then((response) => {
        this.discounts = Object.values(response.data.discounts)
        this.discounts.forEach((discount) => {
          if (!discount.selected) return
          self.addDiscountToCart(discount)
        })
        this.refreshFreeShippingVoucherDisplay()
      })
    },
    addDiscountToCart(discount) {
      if (!this.order.reductions[discount.name]) {
        this.$set(this.order.reductions, discount.name, true)
        return
      }
      this.$delete(this.order.reductions, discount.name)
    },
    getvoucherIcons() {
      if (this.voucher) {
        return 'icon check green'
      }
      return !this.voucher && this.order.voucher ? 'icon close error' : ''
    }
  },
  watch: {
    'order.delivery_address': () => {
      if (
        this.order &&
        this.order.delivery_address.phone_mobile &&
        !this.order.options.shop_delivery
      ) {
        this.order.phone_mobile = this.order.delivery_address.phone_mobile
      }
    },
    // eslint-disable-next-line
    'order.options.shop_delivery': function (val) {
      if (val) {
        this.uncheckFreeShippingVoucherAndDisableIt()
      } else {
        this.refreshFreeShippingVoucherDisplay()
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// OrderModal.vue?32750f28